import { FreightManager, interchangeCells } from '@antification/core';
import { Camera } from './Camera';

/**
 * What a corridor looks like from above, which is two painted cells.
 *
 * An interchange has the same rendering problem a mercy build has, from the other
 * direction: it *is* road, so a district that has built one looks exactly like a
 * district that has not. Law 2 — *a consequence has to be visible somewhere* —
 * means it has to be marked, and the register means the mark is a road marking:
 * two chevrons on the asphalt pointing the way the ramp lies, in the same worn
 * white the parking bays are painted in.
 *
 * Nothing here indicates volume. There is no counter over a ramp, no glow when
 * freight is heavy, no line joining one interchange to another. §12's federal
 * overlay is where freight routes eventually become *"moving dashes"* on a toggled
 * layer (Phase 4); until then the corridor is visible the way everything in
 * Parkington is visible — as traffic, which the player is free to read as traffic.
 *
 * The trucks themselves are `CarRenderer`'s, because a truck is a vehicle.
 */
export class FreightRenderer {
  render(ctx: CanvasRenderingContext2D, camera: Camera, freight: FreightManager): void {
    const interchanges = freight.getInterchanges();
    if (interchanges.length === 0) return;

    const scale = camera.getScale();
    const cellW = camera.cellWidth * scale;
    const cellH = camera.cellHeight * scale;

    ctx.save();
    ctx.strokeStyle = 'rgba(232, 230, 214, 0.62)';
    ctx.lineWidth = Math.max(1, 1.8 * scale);
    ctx.lineCap = 'round';

    for (const structure of interchanges) {
      for (const cell of interchangeCells(structure)) {
        const p = camera.gridToScreen(cell.x, cell.y);
        // A chevron pointing along the structure's axis. Two of them, one per
        // cell, so a two-cell interchange reads as one arrow at any zoom that can
        // resolve a cell at all.
        const along = structure.axis === 'h' ? 1 : 0;
        const w = cellW * 0.2;
        const h = cellH * 0.2;
        ctx.beginPath();
        if (along) {
          ctx.moveTo(p.x - w, p.y - h);
          ctx.lineTo(p.x + w * 0.6, p.y);
          ctx.lineTo(p.x - w, p.y + h);
        } else {
          ctx.moveTo(p.x - w, p.y - h * 0.6);
          ctx.lineTo(p.x, p.y + h);
          ctx.lineTo(p.x + w, p.y - h * 0.6);
        }
        ctx.stroke();
      }
    }

    ctx.restore();
  }
}
